import {
  configureStyle,
  registerCssSerializer,
  scheduleStyleIntent,
  VIEW_CLASS,
} from '@sail/engine';
import {plugins} from './plugins';
import {notUniversalPlugins} from './not_universal_plugins';
import {properties} from './properties';
import {universalSelectorDisabled} from './utils';

declare module '@sail/engine' {
  interface ViewCssSerializerRegistry {
    nonUniversal: typeof css;
  }
}

function createCssSerializer() {
  // Create the serializer
  const css = registerCssSerializer(
    'default',
    configureStyle(properties, plugins),
  );

  // Schedule any styles necessary for normalizing views
  scheduleStyleIntent(
    css.global({
      // https://jira.corp.stripe.com/browse/SAIL-3433
      [`@supports (-webkit-hyphens:none)`]: {
        [`select.${VIEW_CLASS}, select.sail-next-select-fix`]: {
          fontFamily: `'Helvetica Neue', sans-serif !important`,
        },
      },

      [`.${VIEW_CLASS}, .${VIEW_CLASS}::before, .${VIEW_CLASS}::after`]: {
        boxSizing: 'border-box',
        // WebkitFontSmoothing: 'antialiased',
      },
      ...(universalSelectorDisabled
        ? {}
        : {
            [`span.${VIEW_CLASS}`]: {
              display: 'inline',
            },
            [`fieldset.${VIEW_CLASS}`]: {
              border: 0,
              margin: 0,
              minWidth: 0,
              padding: 0,
            },
            [`legend.${VIEW_CLASS}`]: {
              padding: 0,
            },
            [`.${VIEW_CLASS} > option`]: {
              padding: 0,
            },
          }),
    }),
  );

  return css;
}

function createCssNonUniversalSerializer() {
  const css = registerCssSerializer(
    'nonUniversal',
    configureStyle(properties, notUniversalPlugins),
  );

  scheduleStyleIntent(
    css.global({
      [`@supports (-webkit-hyphens:none)`]: {
        [`select.${VIEW_CLASS}, select.sail-next-select-fix`]: {
          fontFamily: `'Helvetica Neue', sans-serif !important`,
        },
      },

      [`.${VIEW_CLASS}, .${VIEW_CLASS}::before, .${VIEW_CLASS}::after`]: {
        boxSizing: 'border-box',
      },
    }),
  );

  return css;
}

/**
 Creates a set of styles that can be applied to one or more views.
 *
 @see https://sail.stripe.me/apis/css
 */
export const css = createCssSerializer();

export const DO_NOT_USE_css = createCssNonUniversalSerializer();

// eslint-disable-next-line @typescript-eslint/no-empty-function
function preserveReference(_value: unknown) {}

/**
 Ensure that APIs that expose the `css` prop reference the default `css`
 serializer method. This ensures that the serializer won't be omitted when
 a bundler eliminates dead code.
 */
export function usesCssSerializer<T>(value: T): T {
  preserveReference(css);
  preserveReference(DO_NOT_USE_css);
  return value;
}

// Add types to the serializer registry to support the `css` prop on views
declare module '@sail/engine' {
  interface ViewCssSerializerRegistry {
    default: typeof css;
  }
}
